/**
 * Per-advisor brand theming.
 *
 * The redesign funnels every brand accent through the `v-brand` Tailwind palette,
 * which (see tailwind.config.js) resolves to `--v-brand-{shade}` CSS variables. The
 * Valur defaults live on `:root` (src/assets/tailwind.css). When an advisory partner
 * sets a custom `brand_color` (hex), we generate an 11-shade ramp from it and override
 * those vars on `<html>` — the whole app re-themes instantly, no rebuild, no per-
 * component logic. Clearing the overrides falls back to the Valur defaults.
 *
 * Partners onboarded before the redesign have no `brand_color` — they picked a stock
 * Tailwind colour *name* in `theme_color`, which the retired `bg-${colorScheme}-500`
 * mechanism used to expand. Those partners fall back to their named colour here, so a
 * subdomain that was gray before the redesign is gray again rather than Valur indigo.
 */

// Relative, not aliased: tests/brand-theme.spec.ts imports this module directly,
// outside the bundler that resolves `@/`.
import { isHousePartner } from './advisoryPartner.js';

const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

// Each shade is the chosen base color (= shade 500) mixed toward white (lighter
// shades) or black (darker shades) by these fractions. Tuned to resemble Tailwind's
// own ramps closely enough for a brand accent.
const MIX = {
    50: ['#ffffff', 0.95],
    100: ['#ffffff', 0.9],
    200: ['#ffffff', 0.75],
    300: ['#ffffff', 0.58],
    400: ['#ffffff', 0.32],
    500: [null, 0],
    600: ['#000000', 0.1],
    700: ['#000000', 0.24],
    800: ['#000000', 0.38],
    900: ['#000000', 0.5],
    950: ['#000000', 0.66],
};

const HEX_RE = /^#?(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Shade 500 of each stock Tailwind palette that `AdvisoryPartner.THEME_COLORS` offers.
// Used both as the fallback ramp base for partners with no `brand_color` and as the
// colour picker's initial value, so the swatch an advisor sees in Workspace settings
// is the colour their site actually renders. `slate` is not in THEME_COLORS but is
// kept for any legacy row that still carries it.
export const THEME_COLOR_HEX = {
    amber: '#f59e0b',
    blue: '#3b82f6',
    cyan: '#06b6d4',
    emerald: '#10b981',
    fuchsia: '#d946ef',
    gray: '#6b7280',
    green: '#22c55e',
    indigo: '#6366f1',
    lime: '#84cc16',
    neutral: '#737373',
    orange: '#f97316',
    pink: '#ec4899',
    purple: '#a855f7',
    red: '#ef4444',
    rose: '#f43f5e',
    sky: '#0ea5e9',
    slate: '#64748b',
    stone: '#78716c',
    teal: '#14b8a6',
    violet: '#8b5cf6',
    yellow: '#eab308',
    zinc: '#71717a',
};

export const DEFAULT_BRAND_HEX = THEME_COLOR_HEX.indigo;

// Text/icon colour that sits ON a brand fill. Valur indigo carries white; a bright
// brand colour (an advisor picking amber or lime) needs dark text instead, which is
// what lets the colour picker offer light shades at all. v-dark-950, the app's
// darkest neutral, rather than pure black.
const ON_BRAND_LIGHT = [255, 255, 255];
const ON_BRAND_DARK = [18, 24, 38];

// Candidate shades for brand-tinted text on the light 100-shade fill, darkest last.
const INK_SHADES = [600, 700, 800, 900, 950];
const AA_CONTRAST = 4.5;

function hexToRgb(hex) {
    const h = hex.replace('#', '');
    const full =
        h.length === 3
            ? h
                  .split('')
                  .map(c => c + c)
                  .join('')
            : h;
    return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
}

function mix(from, to, t) {
    return from.map((c, i) => Math.round(c + (to[i] - c) * t));
}

/** WCAG relative luminance of an [r,g,b] triple. */
function relativeLuminance([r, g, b]) {
    const channel = value => {
        const c = value / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two [r,g,b] triples (1–21). */
function contrastRatio(a, b) {
    const [lighter, darker] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
    return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Pick the legible text colour for a brand fill: whichever of white / v-dark-950
 * contrasts more with it. Exported for the colour picker, which shows the pairing
 * on its swatches so an advisor sees the result before saving.
 */
export function onBrandColor(rgb) {
    return contrastRatio(rgb, ON_BRAND_LIGHT) >= contrastRatio(rgb, ON_BRAND_DARK) ? ON_BRAND_LIGHT : ON_BRAND_DARK;
}

/**
 * Brand-tinted text that has to read on a LIGHT brand surface (the 100-shade fill
 * behind secondary/soft buttons). Walks the dark half of the ramp and takes the
 * first shade that clears WCAG AA against it — with Valur indigo that lands on 600,
 * with a bright brand colour it keeps going until the tint is dark enough.
 */
export function brandInkColor(ramp) {
    const surface = ramp[100];
    const shade = INK_SHADES.find(s => contrastRatio(ramp[s], surface) >= AA_CONTRAST);
    return ramp[shade || INK_SHADES[INK_SHADES.length - 1]];
}

/** Build a { shade: [r,g,b] } ramp from a single base hex (treated as shade 500). */
export function generateRamp(hex) {
    const base = hexToRgb(hex);
    const ramp = {};
    for (const shade of SHADES) {
        const [target, t] = MIX[shade];
        ramp[shade] = target ? mix(base, hexToRgb(target), t) : base;
    }
    return ramp;
}

/** Clear runtime overrides so the `:root` Valur defaults apply. */
export function resetBrandTheme() {
    const root = document.documentElement;
    for (const shade of SHADES) {
        root.style.removeProperty(`--v-brand-${shade}`);
    }
    root.style.removeProperty('--v-brand-on');
    root.style.removeProperty('--v-brand-ink');
}

/**
 * The hex a profile should be themed with: an explicit `brand_color` first, then the
 * legacy `theme_color` preset, then nothing (= the Valur `:root` defaults).
 *
 * The house profile is deliberately excluded: its `theme_color` is `indigo`, and stock
 * Tailwind indigo (#6366f1) is not Valur's tuned indigo (#635bfe), so honouring it
 * would re-theme the main app a shade off its own brand. Exported for the colour
 * picker, which shows this as the current value.
 */
export function resolveBrandHex(profile) {
    if (!profile || isHousePartner(profile)) return null;
    const hex = profile.brand_color || THEME_COLOR_HEX[profile.theme_color];
    return hex && HEX_RE.test(hex) ? hex : null;
}

/**
 * Apply an advisory partner profile's brand color. Accepts the profile object;
 * uses `brand_color` (hex) when present and valid, else the legacy `theme_color`
 * preset, else resets to the Valur defaults. Safe to call repeatedly and with
 * null/undefined.
 */
export function applyBrandTheme(profile) {
    const hex = resolveBrandHex(profile);
    if (!hex) {
        resetBrandTheme();
        return;
    }
    const ramp = generateRamp(hex);
    const root = document.documentElement;
    for (const shade of SHADES) {
        const [r, g, b] = ramp[shade];
        root.style.setProperty(`--v-brand-${shade}`, `${r} ${g} ${b}`);
    }
    // Shade 500 is what filled brand surfaces use (buttons, badges, banners), so the
    // on-brand text colour is derived from it.
    const [onR, onG, onB] = onBrandColor(ramp[500]);
    root.style.setProperty('--v-brand-on', `${onR} ${onG} ${onB}`);
    const [inkR, inkG, inkB] = brandInkColor(ramp);
    root.style.setProperty('--v-brand-ink', `${inkR} ${inkG} ${inkB}`);
}
